/**
 * Sales Agent Nudges Service
 * The proactive chat prompts the Sales Agent shows on a brand's store.
 *
 * Every nudge endpoint is brand scoped by `?bu_id`, writes included: a nudge created while a brand
 * is selected belongs to that brand, and one created with no brand runs on every brand of the
 * account. The field catalog is the exception, it is one list for everybody.
 */

import { api } from '@/lib/api';
import { withBuScope } from '@/lib/business-unit-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { SalesNudgeAdapter } from '@/adapters/sales-nudge.adapter';
import type { ApiResponse } from '@/types';
import type {
	CreateNudgeDto,
	Nudge,
	NudgeField,
	NudgeListResponseDto,
	NudgeResponseDto,
	NudgeStatus,
	UpdateNudgeDto
} from '@/modules/sales-agent/types';

type BuScope = { bu_id?: string };

/**
 * The server's largest page. The list has no pager, so it is read whole: a brand sees its own nudges
 * and the account-wide ones, each capped at 50 separately, so one brand can have up to 100.
 */
export const NUDGE_LIST_LIMIT = 100;

/** Stops a server that keeps reporting a larger total from looping the read forever. */
const NUDGE_LIST_MAX_PAGES = 20;

const hasMorePages = (page: NudgeListResponseDto | null | undefined, loaded: number): boolean =>
	!!page && page.items.length > 0 && loaded < page.total;

const fetchNudgePage = async (scope: BuScope, page: number): Promise<NudgeListResponseDto | null> => {
	const { data } = await api.get<ApiResponse<NudgeListResponseDto>>(API_ENDPOINTS.SALES_NUDGES.BASE, {
		params: { ...scope, page, limit: NUDGE_LIST_LIMIT }
	});
	return data.data ?? null;
};

export const salesNudgesService = {
	async listFields(): Promise<NudgeField[]> {
		const { data } = await api.get<ApiResponse<NudgeField[]>>(API_ENDPOINTS.SALES_NUDGES.FIELDS);
		return SalesNudgeAdapter.fieldsFromApi(data.data);
	},

	/** Every nudge that applies to the scope, read page by page until the server's total is reached. */
	async listNudges(scope: BuScope = {}): Promise<Nudge[]> {
		const nudges: Nudge[] = [];
		for (let page = 1; page <= NUDGE_LIST_MAX_PAGES; page++) {
			const reply = await fetchNudgePage(scope, page);
			nudges.push(...SalesNudgeAdapter.fromListApi(reply));
			if (!hasMorePages(reply, nudges.length)) break;
		}
		return nudges;
	},

	async createNudge(payload: CreateNudgeDto, scope: BuScope = {}): Promise<Nudge> {
		const { data } = await api.post<ApiResponse<NudgeResponseDto>>(
			withBuScope(API_ENDPOINTS.SALES_NUDGES.BASE, scope),
			payload
		);
		return SalesNudgeAdapter.fromApi(data.data);
	},

	async updateNudge(nudgeId: string, payload: UpdateNudgeDto, scope: BuScope = {}): Promise<Nudge> {
		const { data } = await api.patch<ApiResponse<NudgeResponseDto>>(
			withBuScope(API_ENDPOINTS.SALES_NUDGES.BY_ID(nudgeId), scope),
			payload
		);
		return SalesNudgeAdapter.fromApi(data.data);
	},

	async setNudgeStatus(nudgeId: string, status: NudgeStatus, scope: BuScope = {}): Promise<Nudge> {
		const { data } = await api.patch<ApiResponse<NudgeResponseDto>>(
			withBuScope(API_ENDPOINTS.SALES_NUDGES.STATUS(nudgeId), scope),
			{ status }
		);
		return SalesNudgeAdapter.fromApi(data.data);
	},

	async deleteNudge(nudgeId: string, scope: BuScope = {}): Promise<void> {
		await api.delete(withBuScope(API_ENDPOINTS.SALES_NUDGES.BY_ID(nudgeId), scope));
	}
};
